<script setup lang="ts">
import { computed } from 'vue'
import ErrorSvg from '@/svg/error.svg'

const props = defineProps<{ error?: any }>()

// Error type
const isResponseError = computed(() => props.error?.response !== undefined)
const isRequestError = computed(() => props.error?.request !== undefined)

// Valid for response errors: has details?
const hasErrorDetail = computed(() => props?.error?.response?.data?.detail)
const hasErrorDetails = computed(() => props.error?.response?.data?.details?.length > 0)
</script>

<template>
  <div class="error-result">
    <el-result class="error-result__content">
      <template #icon>
        <ErrorSvg />
      </template>
      <template #title>
        <p v-if="isResponseError">HTTP {{ error.response.status }} Error</p>
        <p v-else-if="isRequestError">An error occurred while making your request.</p>
        <p v-else>An unknown error occured.</p>
      </template>
      <template #sub-title>
        <template v-if="isResponseError">
          <div class="error-result__description">
            <p v-if="error.response?.data?.description">{{ error.response.data.description }}</p>
            <p v-else>No error description provided.</p>
          </div>
        </template>
        <p v-else-if="isRequestError">Check your internet connection or VPN status.</p>
        <p v-else>Sorry about that.</p>
      </template>
      <template #extra>
        <el-alert v-if="hasErrorDetails || hasErrorDetail" class="error-result__details" :closable="false">
          <ul v-if="hasErrorDetails">
            <li v-for="detail in error.response.data.details" :key="detail">
              {{ detail }}
            </li>
          </ul>
          <template v-else-if="hasErrorDetail">
            {{ error.response.data.detail }}
          </template>
        </el-alert>
        <div class="error-result__actions">
          <slot></slot>
        </div>
      </template>
    </el-result>
  </div>
</template>

<style scoped lang="scss">
::v-deep(.el-result__extra) {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-spacing-sm);
  margin-top: 0;
  text-align: start;
  width: 100%;
}

::v-deep(.el-alert__description) {
  margin-top: 0;
}

.error-result {
  display: flex;
  flex-direction: column;
  align-items: center;

  &__description {
    display: inline-flex;
    align-items: center;
    gap: var(--spacing-spacing-xs);
  }

  &__details {
    width: 100%;
    max-width: 600px;

    ul {
      list-style: square;
    }
  }

  &__actions {
    display: inline-flex;
    justify-content: center;
  }
}

/* Override ElResult icon dimensions */
.el-result__icon svg {
  height: initial;
  width: initial;
}

/* End override */
</style>
